// DESCRIPTION: Main settings app entry point that renders the tabbed settings UI
// with email, payment, event types, and integrations tabs.

import { useState, useEffect, useCallback } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import {
    Button,
    Notice,
    Spinner,
} from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';
import Header from './components/Header';
import EmailSettings from './components/EmailSettings';
import PaymentSettings from './components/PaymentSettings';
import EventTypesSettings from './components/EventTypesSettings';
import GoogleCalendarSettings from './components/GoogleCalendarSettings';

/**
 * Settings App Component
 *
 * Main component for managing plugin settings
 */
function SettingsApp() {
    const [settings, setSettings] = useState({});
    const [initialSettings, setInitialSettings] = useState({});
    const [eventTypes, setEventTypes] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [notices, setNotices] = useState([]);
    const [activeTab, setActiveTab] = useState('email');

    // Dirty state — true when settings differ from what was last saved/loaded
    const isDirty = JSON.stringify(settings) !== JSON.stringify(initialSettings);

    // Warn before leaving with unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                e.preventDefault();
                e.returnValue = '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    // Cmd+S / Ctrl+S keyboard shortcut to save
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault();

                if (isDirty && !isSaving && activeTab !== 'event-types') {
                    saveSettings();
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isDirty, isSaving, activeTab, settings]);

    // Load initial data
    useEffect(() => {
        loadData();
    }, []);

    /**
     * Load settings and event types from API
     */
    const loadData = async () => {
        setIsLoading(true);
        try {
            const [settingsResponse, eventTypesResponse] = await Promise.all([
                apiFetch({ path: '/nemtly-booking/v1/settings' }),
                apiFetch({ path: '/nemtly-booking/v1/settings/event-types' }),
            ]);

            if (settingsResponse && settingsResponse.success) {
                const loadedSettings = settingsResponse.data || settingsResponse;
                setSettings(loadedSettings);
                setInitialSettings(loadedSettings);
            }

            if (eventTypesResponse && eventTypesResponse.success) {
                setEventTypes(eventTypesResponse.data || eventTypesResponse);
            }
        } catch (error) {
            addNotice(__('Failed to load settings', 'nemtly-booking'), 'error');
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Save settings
     */
    const saveSettings = async () => {
        setIsSaving(true);
        try {
            const response = await apiFetch({
                path: '/nemtly-booking/v1/settings',
                method: 'POST',
                data: settings,
            });

            if (response && response.success) {
                addNotice(__('Settings saved successfully', 'nemtly-booking'), 'success');
                const savedSettings = response.data || response;
                setSettings(savedSettings);
                setInitialSettings(savedSettings);
            } else {
                addNotice(__('Failed to save settings', 'nemtly-booking'), 'error');
            }
        } catch (error) {
            addNotice(__('Failed to save settings', 'nemtly-booking'), 'error');
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Add notice message
     */
    const addNotice = (message, type = 'info') => {
        const notice = { message, type, id: Date.now() };
        setNotices(prev => [...prev, notice]);

        // Auto-remove notice after 4 seconds
        setTimeout(() => {
            setNotices(prev => prev.filter(n => n.id !== notice.id));
        }, 4000);
    };

    /**
     * Update setting value
     */
    const updateSetting = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: value
        }));
    };

    /**
     * Remove notice
     */
    const removeNotice = (noticeId) => {
        setNotices(prev => prev.filter(n => n.id !== noticeId));
    };

    /**
     * Switch tab with unsaved changes guard
     */
    const switchTab = useCallback((tabId) => {
        if (isDirty) {
            const confirmSwitch = window.confirm(
                __('You have unsaved changes. Are you sure you want to switch tabs? Your changes will be lost.', 'nemtly-booking')
            );

            if (!confirmSwitch) {
                return;
            }

            // Revert to saved state when discarding
            setSettings(initialSettings);
        }

        setActiveTab(tabId);
    }, [isDirty, initialSettings]);

    if (isLoading) {
        return (
            <div className="nemtly-booking-settings-loading">
                <Spinner />
                <p>{__('Loading settings...', 'nemtly-booking')}</p>
            </div>
        );
    }

    return (
        <div className="nb-settings-app">
            {/* Header */}
            <Header
                version={nemtlySettings?.version || ''}
                pageName={__('Settings', 'nemtly-booking')}
            />

            {/* Notices */}
            <div className="nb-p-6">
                <div className="nb-space-y-3 nb-mb-8">
                    {notices.map(notice => (
                        <Notice
                            key={notice.id}
                            status={notice.type}
                            onRemove={() => removeNotice(notice.id)}
                            isDismissible
                            className="nb-rounded-lg"
                        >
                            {notice.message}
                        </Notice>
                    ))}
                </div>

                {/* Modern Tab Navigation */}
                <div className="nb-mb-8">
                    <div className="nb-border-b nb-border-slate-200">
                        <nav className="nb-flex nb-space-x-8" aria-label="Settings tabs">
                            {[
                                {
                                    id: 'email',
                                    label: __('Email Settings', 'nemtly-booking'),
                                    icon: <svg className="nb-w-4 nb-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
                                },
                                {
                                    id: 'payment',
                                    label: __('Payment', 'nemtly-booking'),
                                    icon: <svg className="nb-w-4 nb-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>,
                                },
                                {
                                    id: 'event-types',
                                    label: __('Event Types', 'nemtly-booking'),
                                    icon: <svg className="nb-w-4 nb-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>,
                                },
                                {
                                    id: 'integrations',
                                    label: __('Integrations', 'nemtly-booking'),
                                    icon: <svg className="nb-w-4 nb-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>,
                                },
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => switchTab(tab.id)}
                                    className={`nb-flex nb-items-center nb-gap-2 nb-py-3 nb-px-1 nb-border-b-2 nb-font-medium nb-text-sm nb-transition-colors nb-duration-200 ${activeTab === tab.id
                                            ? 'nb-border-slate-600 nb-text-slate-600'
                                            : 'nb-border-transparent nb-text-slate-500 hover:nb-text-slate-700 hover:nb-border-slate-300'
                                        }`}
                                >
                                    {tab.icon}
                                    <span>{tab.label}</span>
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                {/* Tab Content */}
                <div className="nb-settings-content">
                    {activeTab === 'email' && (
                        <EmailSettings
                            settings={settings}
                            updateSetting={updateSetting}
                        />
                    )}

                    {activeTab === 'payment' && (
                        <PaymentSettings
                            settings={settings}
                            updateSetting={updateSetting}
                            addNotice={addNotice}
                        />
                    )}

                    {activeTab === 'event-types' && (
                        <EventTypesSettings
                            eventTypes={eventTypes}
                            onRefresh={loadData}
                            addNotice={addNotice}
                        />
                    )}

                    {activeTab === 'integrations' && (
                        <GoogleCalendarSettings
                            settings={settings}
                            updateSetting={updateSetting}
                            addNotice={addNotice}
                        />
                    )}
                </div>

                {/* Sticky Save Bar — visible when there are unsaved changes */}
                {activeTab !== 'event-types' && isDirty && (
                    <div
                        className="nb-fixed nb-bottom-0 nb-left-0 nb-right-0 nb-bg-white nb-border-t nb-border-slate-200 nb-shadow-lg nb-z-50"
                        style={{ marginLeft: 'var(--wp-admin--sidebar--width, 160px)' }}
                    >
                        <div className="nb-max-w-7xl nb-mx-auto nb-px-6 nb-py-3 nb-flex nb-items-center nb-justify-between">
                            <div className="nb-flex nb-items-center nb-gap-2">
                                <div className="nb-w-2 nb-h-2 nb-bg-amber-500 nb-rounded-full"></div>
                                <span className="nb-text-sm nb-text-slate-600">
                                    {__('You have unsaved changes', 'nemtly-booking')}
                                </span>
                            </div>
                            <div className="nb-flex nb-items-center nb-gap-3">
                                <Button
                                    variant="tertiary"
                                    onClick={() => {
                                        setSettings(initialSettings);
                                    }}
                                    disabled={isSaving}
                                >
                                    {__('Discard', 'nemtly-booking')}
                                </Button>
                                <Button
                                    onClick={saveSettings}
                                    isBusy={isSaving}
                                    disabled={isSaving}
                                    className="nb-settings-button-primary nb-px-6 nb-py-2.5 nb-text-white"
                                >
                                    {isSaving ? __('Saving...', 'nemtly-booking') : __('Save Settings', 'nemtly-booking')}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// Initialize the app
document.addEventListener('DOMContentLoaded', function () {
    const settingsContainer = document.getElementById('nemtly-booking-settings-app');
    if (settingsContainer) {
        wp.element.render(wp.element.createElement(SettingsApp), settingsContainer);
    }
});
